/**
 * Iter C.2.1.e — Special Offer Display (read-only, PO Detail page).
 *
 * Renders the frozen Special Offer snapshot on a Proposal-derived PO
 * with a clear "Commercially Frozen" indicator + lineage trace to the
 * source proposal version. Per Rahul's 2026-02 design decisions, this
 * is purely read-only — re-negotiation on a Proposal-derived PO is
 * prohibited (§6.2 hard-block).
 *
 * Reuses the same three-row block geometry as the Proposal editor for
 * visual consistency. The INTERNAL `reason_code` is exposed only to
 * the internal operator view — never on client artifacts (PO PDF /
 * invoice).
 *
 * Standalone POs (no `from_proposal_id`) follow a different branch:
 * an authoring control could live here in a future iter, but for v1
 * the standalone-PO authoring path is out of scope.
 */
import React from "react";
import { Lock, Tag } from "@phosphor-icons/react";
import { formatINR } from "../../lib/format";

export function SpecialOfferDisplay({ po }) {
    const offer = po?.special_offer || null;
    if (!offer || !offer.enabled) {
        // No offer — render nothing on the PO surface (the standard
        // commercial layout stays clean).
        return null;
    }
    const isProposalDerived = Boolean(po?.from_proposal_id);
    const currency = po?.currency;
    const standard = Number(offer.standard_value_at_apply || 0);
    const discount = Number(offer.discount_amount || 0);
    const net = Number(offer.net_value_at_apply || 0);

    return (
        <div className="reedos-card" data-testid="po-special-offer-card">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    {isProposalDerived ? (
                        <Lock size={14} className="text-amber-600" weight="fill" />
                    ) : (
                        <Tag size={14} className="text-slate-600" />
                    )}
                    <span className="reedos-eyebrow text-slate-700">
                        Special Offer{isProposalDerived ? " · Frozen" : ""}
                    </span>
                </div>
                {isProposalDerived && po?.from_proposal_number && (
                    <span
                        className="text-[10px] text-slate-500"
                        data-testid="po-special-offer-lineage"
                    >
                        Snapshot from {po.from_proposal_number}
                        {po.from_proposal_version_number != null ? ` V${po.from_proposal_version_number}` : ""}
                    </span>
                )}
            </div>
            <div className="px-5 py-4 space-y-3">
                <div>
                    <div className="reedos-eyebrow text-slate-500 mb-0.5">Client-facing label</div>
                    <div
                        className="text-sm font-medium text-slate-900"
                        data-testid="po-special-offer-label"
                    >
                        {offer.label}
                    </div>
                    {/* UAT 45.6.6 (2026-07-14) — offer description surfaces under
                        the label whenever the standardized offer taxonomy is used. */}
                    {offer.description && (
                        <div
                            className="text-xs text-slate-600 mt-1"
                            data-testid="po-special-offer-description"
                        >
                            {offer.description}
                        </div>
                    )}
                </div>
                <div className="grid grid-cols-2 gap-3 text-xs">
                    <div>
                        <div className="reedos-eyebrow text-slate-500 mb-0.5">Discount</div>
                        <div className="text-sm">
                            {offer.discount_type === "Percentage"
                                ? `${offer.discount_value}%`
                                : "Fixed amount"}
                            <span className="text-slate-500 ml-1.5">
                                — {formatINR(discount, currency)}
                            </span>
                        </div>
                    </div>
                    <div>
                        <div className="reedos-eyebrow text-slate-500 mb-0.5">
                            Reason · internal
                        </div>
                        <div
                            className="text-sm font-medium text-indigo-700"
                            data-testid="po-special-offer-reason"
                        >
                            {offer.reason_code}
                        </div>
                    </div>
                </div>
                <div
                    className="rounded-md border border-slate-200 bg-slate-50/50 px-4 py-3 text-xs space-y-1.5"
                    data-testid="po-special-offer-three-row"
                >
                    <div className="flex justify-between text-slate-700">
                        <span>Standard Commercial Value</span>
                        <span className="reedos-data">{formatINR(standard, currency)}</span>
                    </div>
                    <div className="flex justify-between text-rose-700 font-medium">
                        <span>Less: Special Offer</span>
                        <span className="reedos-data">−{formatINR(discount, currency)}</span>
                    </div>
                    <div className="flex justify-between border-t border-slate-300 pt-1.5 text-slate-900 font-display font-semibold">
                        <span>Net Commercial Value</span>
                        <span className="reedos-data">{formatINR(net, currency)}</span>
                    </div>
                </div>
                {offer.applied_at && (
                    <div className="text-[10px] text-slate-400 pt-1">
                        Applied {offer.applied_at.substring(0, 10)}
                        {offer.applied_by_email ? ` by ${offer.applied_by_email}` : ""}
                    </div>
                )}
            </div>
        </div>
    );
}

export default SpecialOfferDisplay;
